import { Button, ErrorDisplay, Icon } from "@/shared/components";
import { cn, normalizeError } from "@/shared/utils";
import { mergeRefs } from "@/shared/utils/merge-ref.ts";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ReactNode, useCallback, useRef, useState } from "react";

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return <DialogPrimitive.Root data-slot="dialog" data-testid="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" data-testid="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" data-testid="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
    return <DialogPrimitive.Close data-slot="dialog-close" data-testid="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            data-testid="dialog-overlay"
            className={cn(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-overlay fixed inset-0 z-50",
                className
            )}
            {...props}
        />
    );
}

type DialogContainerProps = React.ComponentProps<typeof DialogPrimitive.Content> & {
    dismissible?: boolean;
};

function DialogContainer({ className, children, dismissible = true, ref, ...props }: DialogContainerProps) {
    const dismissAttempts = useRef(0);
    const dialogContentRef = useRef<HTMLDivElement | null>(null);
    const [dimensions, setDimensions] = useState<{ width: number; height: number }>();
    const [wiggleKey, setWiggleKey] = useState(0);

    const wiggle = (e: Event) => {
        e.preventDefault();
        // Instead of boolean, use a "key" so we don't switch back to the animate-in/out and fade-in/out animation
        // after the wiggle animation is finished. This way we still have normal animations on opening
        // but wiggle animations for non-dismissable dialogs. The Content is rerendered because of the key-prop
        dismissAttempts.current++;
        setWiggleKey(dismissAttempts.current);
    };

    /* Refs with radix seem to work a bit strange, but the callback + merge method works! */
    const setRefDimensions = useCallback(
        (el: HTMLDivElement | null) => {
            dialogContentRef.current = el;
            if (el !== null && !dimensions) {
                const { width, height } = el.getBoundingClientRect();
                setDimensions({ width, height });
            }
        },
        [dimensions]
    );

    const onEscapeKeyDown = useCallback((e: KeyboardEvent) => {
        const nestedComboBoxOpen =
            dialogContentRef.current && dialogContentRef.current.querySelectorAll("[data-combobox-popup][data-open]").length > 0;
        if (nestedComboBoxOpen) {
            // prevent default behaviour (closing dialog) when there is a combobox open inside this dialog
            e.preventDefault();
        }
    }, []);

    return (
        <DialogPortal data-slot="dialog-portal" data-testid="dialog-portal">
            <DialogOverlay />
            <DialogPrimitive.Content
                key={wiggleKey}
                ref={mergeRefs(setRefDimensions, ref)}
                data-slot="dialog-content"
                data-testid="dialog-content"
                className={cn(
                    "bg-elevated-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 fixed top-[50%] left-[50%] z-50 grid max-h-[95vh] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] overflow-auto rounded-lg border shadow-lg duration-200 sm:max-w-(--dialog-max-width)",
                    wiggleKey === 0 && "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
                    wiggleKey > 0 && "animate-wiggle",
                    dimensions?.height && dimensions.height < 500 ? "overflow-visible" : "overflow-auto",
                    className
                )}
                onEscapeKeyDown={onEscapeKeyDown}
                onPointerDownOutside={dismissible ? undefined : wiggle}
                {...props}
            >
                {children}
                {dismissible && (
                    <DialogPrimitive.Close
                        data-testid="close-dialog"
                        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                    >
                        <Icon name="close" className="text-content-subtle" />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="dialog-header"
            data-testid="dialog-header"
            className={cn("flex flex-col gap-2 border-b px-6 py-5 text-center sm:text-left", className)}
            {...props}
        />
    );
}

function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
    return <div data-slot="dialog-body" data-testid="dialog-body" className={cn("p-6 pb-8", className)} {...props} />;
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="dialog-footer"
            data-testid="dialog-footer"
            className={cn("flex justify-end gap-2 border-t px-6 py-4", className)}
            {...props}
        />
    );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            data-testid="dialog-title"
            className={cn("text-content-emphasis text-xl leading-none font-medium", className)}
            {...props}
        />
    );
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            data-testid="dialog-description"
            className={cn("text-muted-foreground text-m", className)}
            {...props}
        />
    );
}

type BaseConfirmDialogProps = {
    isOpen: boolean;
    onCancel: () => void;
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    children?: ReactNode;
    destructive?: boolean;
};

type SyncConfirmDialogProps = BaseConfirmDialogProps & {
    async?: false;
    onConfirm: () => void;
    isPending: boolean;
    error: Error | null;
};

type AsyncConfirmDialogProps = BaseConfirmDialogProps & {
    async: true;
    onConfirm: () => Promise<void>;
};

type ConfirmDialogProps = SyncConfirmDialogProps | AsyncConfirmDialogProps;

/**
 * ConfirmDialog supports two modes:
 * 1. Sync mode (async?: false, the default): Pass isPending, error and sync onConfirm props - useful when using external mutation state
 * 2. Async mode (async: true): Pass async onConfirm - component manages isPending and error internally
 */
function ConfirmDialog(props: Readonly<ConfirmDialogProps>) {
    const [errorAsync, setErrorAsync] = useState<Error | null>(null);
    const [isPendingAsync, setIsPendingAsync] = useState(false);

    const {
        isOpen,
        title = "Weet je het zeker?",
        confirmLabel = "Bevestigen",
        cancelLabel = "Annuleren",
        onCancel,
        children,
        destructive = false,
    } = props;

    const handleConfirmAsync = async () => {
        try {
            setErrorAsync(null);
            setIsPendingAsync(true);
            await props.onConfirm();
        } catch (error) {
            setErrorAsync(normalizeError(error));
        } finally {
            setIsPendingAsync(false);
        }
    };

    const async = !!props.async;
    const error = async ? errorAsync : props.error;
    const isPending = async ? isPendingAsync : props.isPending;
    const handleConfirm = async ? handleConfirmAsync : props.onConfirm;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && !isPending && onCancel()}>
            <DialogContainer data-testid="confirmation-dialog">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    {children && <DialogDescription>{children}</DialogDescription>}
                    {error && (
                        <div className="pt-3">
                            <ErrorDisplay error={error} />
                        </div>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button variant="tertiary" onClick={onCancel} disabled={isPending} data-testid="cancel">
                        {cancelLabel}
                    </Button>
                    <Button variant="primary" onClick={handleConfirm} destructive={destructive} disabled={isPending} data-testid="confirm">
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContainer>
        </Dialog>
    );
}

export {
    ConfirmDialog,
    Dialog,
    DialogBody,
    DialogClose,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
